import {
    ArrowDropDown,
    Download,
    Fullscreen,
    FullscreenExit,
    KeyboardArrowDown,
    KeyboardArrowUp,
    Print,
    ZoomIn,
    ZoomOut,
} from '@mui/icons-material';
import {
    Box,
    Button,
    IconButton,
    Menu,
    MenuItem,
    TextField,
    Tooltip,
    Typography,
} from '@mui/material';
import { useState } from 'react';

/** A numeric scale (1 = 100%) or a fit mode computed from the container size. */
export type ZoomLevel = number | 'page-width' | 'page-fit';

interface ZoomMenuOption {
    label: string;
    value: ZoomLevel;
}

const ZOOM_MENU_OPTIONS: ZoomMenuOption[] = [
    { label: 'Actual size', value: 1 },
    { label: 'Page fit', value: 'page-fit' },
    { label: 'Page width', value: 'page-width' },
    { label: '50%', value: 0.5 },
    { label: '75%', value: 0.75 },
    { label: '100%', value: 1 },
    { label: '125%', value: 1.25 },
    { label: '150%', value: 1.5 },
    { label: '200%', value: 2 },
    { label: '300%', value: 3 },
    { label: '400%', value: 4 },
];

interface Props {
    currentPage: number;
    numPages: number;
    zoomPercent: number;
    disabled: boolean;
    isFullscreen: boolean;
    showFullscreenButton: boolean;
    isPrinting: boolean;
    onGoToPage(page: number): void;
    onZoomIn(): void;
    onZoomOut(): void;
    onSelectZoom(zoom: ZoomLevel): void;
    onToggleFullscreen(): void;
    onDownload(): void;
    onPrint(): void;
}

export default function PdfViewerToolbar({
    currentPage,
    numPages,
    zoomPercent,
    disabled,
    isFullscreen,
    showFullscreenButton,
    isPrinting,
    onGoToPage,
    onZoomIn,
    onZoomOut,
    onSelectZoom,
    onToggleFullscreen,
    onDownload,
    onPrint,
}: Props) {
    const [pageInput, setPageInput] = useState(String(currentPage));
    const [lastSyncedPage, setLastSyncedPage] = useState(currentPage);
    const [zoomMenuAnchor, setZoomMenuAnchor] = useState<HTMLElement | null>(null);

    // Keep the page input in sync when the current page changes from scrolling.
    if (currentPage !== lastSyncedPage) {
        setLastSyncedPage(currentPage);
        setPageInput(String(currentPage));
    }

    function commitPageInput() {
        const parsed = Number.parseInt(pageInput, 10);
        if (Number.isNaN(parsed)) {
            setPageInput(String(currentPage));
            return;
        }
        const clamped = Math.min(Math.max(parsed, 1), Math.max(numPages, 1));
        setPageInput(String(clamped));
        onGoToPage(clamped);
    }

    function handleSelectZoom(zoom: ZoomLevel) {
        setZoomMenuAnchor(null);
        onSelectZoom(zoom);
    }

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', width: '100%', gap: 0.5 }}>
            {/* Page navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', flex: 1, gap: 0.5 }}>
                <Tooltip title="Previous page">
                    <span>
                        <IconButton
                            size="small"
                            disabled={disabled || currentPage <= 1}
                            onClick={() => onGoToPage(currentPage - 1)}
                        >
                            <KeyboardArrowUp fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <TextField
                    value={pageInput}
                    disabled={disabled}
                    size="small"
                    onChange={(event) => setPageInput(event.target.value)}
                    onBlur={commitPageInput}
                    onKeyDown={(event) => {
                        if (event.key === 'Enter') commitPageInput();
                    }}
                    inputProps={{
                        'aria-label': 'Page number',
                        style: { textAlign: 'center', padding: '4px' },
                    }}
                    sx={{ width: 44 }}
                />
                <Typography variant="body2" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    / {numPages}
                </Typography>
                <Tooltip title="Next page">
                    <span>
                        <IconButton
                            size="small"
                            disabled={disabled || currentPage >= numPages}
                            onClick={() => onGoToPage(currentPage + 1)}
                        >
                            <KeyboardArrowDown fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Zoom */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Tooltip title="Zoom out">
                    <span>
                        <IconButton size="small" disabled={disabled} onClick={onZoomOut}>
                            <ZoomOut fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Button
                    size="small"
                    color="inherit"
                    disabled={disabled}
                    onClick={(event) => setZoomMenuAnchor(event.currentTarget)}
                    endIcon={<ArrowDropDown />}
                    sx={{ minWidth: 72, textTransform: 'none' }}
                >
                    {zoomPercent}%
                </Button>
                <Menu
                    anchorEl={zoomMenuAnchor}
                    open={Boolean(zoomMenuAnchor)}
                    onClose={() => setZoomMenuAnchor(null)}
                >
                    {ZOOM_MENU_OPTIONS.map((option) => (
                        <MenuItem key={option.label} onClick={() => handleSelectZoom(option.value)}>
                            {option.label}
                        </MenuItem>
                    ))}
                </Menu>
                <Tooltip title="Zoom in">
                    <span>
                        <IconButton size="small" disabled={disabled} onClick={onZoomIn}>
                            <ZoomIn fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>

            {/* Actions */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flex: 1,
                    justifyContent: 'flex-end',
                    gap: 0.5,
                }}
            >
                {showFullscreenButton && (
                    <Tooltip title={isFullscreen ? 'Exit full screen' : 'Full screen'}>
                        <span>
                            <IconButton
                                size="small"
                                disabled={disabled}
                                onClick={onToggleFullscreen}
                            >
                                {isFullscreen ? (
                                    <FullscreenExit fontSize="small" />
                                ) : (
                                    <Fullscreen fontSize="small" />
                                )}
                            </IconButton>
                        </span>
                    </Tooltip>
                )}
                <Tooltip title="Download">
                    <span>
                        <IconButton size="small" disabled={disabled} onClick={onDownload}>
                            <Download fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
                <Tooltip title="Print">
                    <span>
                        <IconButton
                            size="small"
                            disabled={disabled || isPrinting}
                            onClick={onPrint}
                        >
                            <Print fontSize="small" />
                        </IconButton>
                    </span>
                </Tooltip>
            </Box>
        </Box>
    );
}
